import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import utc from 'dayjs/plugin/utc';
import { I18n } from 'react-redux-i18n';
import { formatDateTime, FormatDateTimeOptions } from './formatDateTime';

dayjs.extend(isSameOrAfter);
dayjs.extend(utc);

export type TimeInput = Date | string | number | Dayjs | null | undefined;

type TimeUnit = 'year' | 'month' | 'week' | 'day' | 'hour' | 'minute';

type UnitMeta = {
  key: TimeUnit;
  diff: TimeUnit;
  add: 'years' | 'months' | 'weeks' | 'days' | 'hours' | 'minutes';
};

const ORDERED_UNITS: UnitMeta[] = [
  { key: 'year', diff: 'year', add: 'years' },
  { key: 'month', diff: 'month', add: 'months' },
  { key: 'week', diff: 'week', add: 'weeks' },
  { key: 'day', diff: 'day', add: 'days' },
  { key: 'hour', diff: 'hour', add: 'hours' },
  { key: 'minute', diff: 'minute', add: 'minutes' }
];

export interface FormatTimeElapsedOptions {
  /** Maximum number of units to display (e.g. year, month). Defaults to 2. */
  maxUnits?: number;
  /**
   * Whether to append the "ago" style suffix. Defaults to true. When false, the formatter
   * behaves like a pure duration formatter.
   */
  includeSuffix?: boolean;
  /** Value to return when no meaningful duration can be computed. Defaults to an em dash. */
  fallback?: string;
  /**
   * Whether to treat the input as UTC. Defaults to false.
   * When true, the input string is interpreted as UTC time (e.g., "2026-01-12T05:24:53").
   * When false, the input is parsed as local time.
   */
  isUtc?: boolean;
  /**
   * Whether the result is being embedded in a sentence rather than standing alone. Defaults to
   * false. The word-only results are capitalised for standalone use, so a caller interpolating
   * one mid-sentence needs the lowercase form.
   */
  inSentence?: boolean;
}

type UnitSegment = {
  unit: TimeUnit;
  value: number;
};

const buildSegments = (start: Dayjs, end: Dayjs): UnitSegment[] => {
  const segments: UnitSegment[] = [];
  let cursor = start.clone();

  ORDERED_UNITS.forEach(({ key, diff, add }) => {
    const amount = end.diff(cursor, diff);
    if (amount > 0) {
      segments.push({ unit: key, value: amount });
      cursor = cursor.add(amount, add);
    }
  });

  return segments;
};

const getUnitLabel = (unit: TimeUnit, value: number): string => {
  const baseKey = `general.time-ago.units.${unit}`;
  const pluralKey = value === 1 ? baseKey : `${baseKey}_plural`;
  return I18n.t(pluralKey, { count: value });
};

// The word-only results are capitalised for standalone display. Every other result starts with a
// digit, so this is the whole difference between the standalone and in-sentence forms.
const relativeKeys = (inSentence: boolean) => ({
  recently: inSentence
    ? 'general.time-ago.recently-in-sentence'
    : 'general.time-ago.recently',
  lessThanMinute: inSentence
    ? 'general.time-ago.less-than-minute-in-sentence'
    : 'general.time-ago.less-than-minute'
});

const formatSegments = (
  segments: UnitSegment[],
  includeSuffix: boolean,
  maxUnits: number,
  inSentence: boolean
): string => {
  const keys = relativeKeys(inSentence);
  if (!segments.length) {
    return includeSuffix ? I18n.t(keys.recently) : I18n.t(keys.lessThanMinute);
  }

  const effectiveUnits = Math.max(1, Math.min(maxUnits, segments.length));
  const selected = segments.slice(0, effectiveUnits);
  const separator = I18n.t('general.time-ago.separator');
  const listDelimiter = I18n.t('general.time-ago.list-delimiter');
  const suffix = I18n.t('general.time-ago.suffix');

  const labels = selected.map(({ unit, value }) => getUnitLabel(unit, value));
  let result: string;

  if (labels.length === 1) {
    result = labels[0];
  } else if (labels.length === 2) {
    result = `${labels[0]} ${separator} ${labels[1]}`;
  } else {
    const last = labels.pop();
    result = `${labels.join(listDelimiter)} ${separator} ${last}`;
  }

  return includeSuffix ? `${result} ${suffix}`.trim() : result;
};

export const formatTimeElapsed = (
  startInput: TimeInput,
  endInput?: TimeInput,
  options: FormatTimeElapsedOptions = {}
): string => {
  const {
    maxUnits = 2,
    includeSuffix = true,
    fallback = '—',
    isUtc = false,
    inSentence = false
  } = options;
  const keys = relativeKeys(inSentence);

  if (!startInput) {
    return fallback;
  }

  // When isUtc is true, parse as UTC then convert to local for comparison with now
  const start = isUtc ? dayjs.utc(startInput).local() : dayjs(startInput);
  const end = endInput
    ? (isUtc ? dayjs.utc(endInput).local() : dayjs(endInput))
    : dayjs();

  if (!start.isValid() || !end.isValid()) {
    return fallback;
  }

  if (!includeSuffix && end.isBefore(start)) {
    // When formatting pure durations we treat negative intervals as zero.
    return I18n.t(keys.lessThanMinute);
  }

  if (includeSuffix && !end.isSameOrAfter(start)) {
    return I18n.t(keys.recently);
  }

  const diffMs = end.diff(start);
  if (diffMs < 60 * 1000) {
    return includeSuffix ? I18n.t(keys.recently) : I18n.t(keys.lessThanMinute);
  }

  const segments = buildSegments(start, end);
  return formatSegments(segments, includeSuffix, maxUnits, inSentence);
};

export interface FormatTimeElapsedOrDateOptions {
  /** Number of days after which to switch from relative time to absolute date. Defaults to 7. */
  switchToDateAfter?: number;
  /** Options to pass to formatDateTime when displaying absolute dates. */
  dateFormatOptions?: FormatDateTimeOptions;
  /** Options to pass to formatTimeElapsed when displaying relative time. */
  relativeTimeOptions?: FormatTimeElapsedOptions;
}

/**
 * Formats elapsed time, automatically switching to absolute date format after a threshold.
 * 
 * Use this function when you want to show either relative time OR absolute date, not both:
 * - Within threshold: Shows relative time (e.g., "3 days ago", "2 hours ago")
 * - Beyond threshold: Shows absolute date (e.g., "13 Mar 2025, 1:30 pm")
 * 
 * This avoids redundant long relative times like "8 months and 2 weeks ago" by showing
 * the actual date instead.
 * 
 * @param startInput - The start date/time (when the event occurred)
 * @param endInput - Optional end date/time for comparison (defaults to now)
 * @param options - Configuration options
 * @returns Formatted relative time string or absolute date
 * 
 * @example
 * // Within 7 days - shows relative time
 * formatTimeElapsedOrDate(threeDaysAgo) // "3 days ago"
 * 
 * @example
 * // Beyond 7 days - shows absolute date
 * formatTimeElapsedOrDate(eightMonthsAgo) // "13 Mar 2025, 1:30 pm"
 * 
 * @example
 * // Custom threshold
 * formatTimeElapsedOrDate(date, undefined, { switchToDateAfter: 30 })
 * 
 * @example
 * // Custom date formatting
 * formatTimeElapsedOrDate(date, undefined, { 
 *   dateFormatOptions: { includeTime: false }
 * })
 */
export const formatTimeElapsedOrDate = (
  startInput: TimeInput,
  endInput?: TimeInput,
  options: FormatTimeElapsedOrDateOptions = {}
): string => {
  const {
    switchToDateAfter = 7,
    dateFormatOptions = {},
    relativeTimeOptions = {}
  } = options;

  if (!startInput) {
    return relativeTimeOptions.fallback ?? '—';
  }

  const start = dayjs(startInput);
  const end = endInput ? dayjs(endInput) : dayjs();

  if (!start.isValid() || !end.isValid()) {
    return relativeTimeOptions.fallback ?? '—';
  }

  const daysDiff = end.diff(start, 'day');

  // Beyond threshold - show absolute date instead of long relative time
  if (daysDiff > switchToDateAfter) {
    return formatDateTime(start, dateFormatOptions);
  }

  // Within threshold - show relative time
  return formatTimeElapsed(startInput, endInput, relativeTimeOptions);
};

export default formatTimeElapsed;
